/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { get as cookieGet } from 'js/lib/cookie';
import { isAuthenticatedUser, get as userGet } from 'js/lib/user';

import { IconButton, MenuList, Popover, Tag } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import IconButtonWrapper from 'bundles/composable-page-header/components/IconButtonWrapper';
import LanguageTranslationMenuItem from 'bundles/composable-page-header/components/translation/LanguageTranslationMenuItem';
import { DEFAULT_ICON_BUTTON_HEIGHT, DEFAULT_ICON_BUTTON_SVG_HEIGHT } from 'bundles/composable-page-header/constants';
import type { AccountDropdownLink } from 'bundles/composable-page-header/types';
import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';
import UserPortrait from 'bundles/page-header/components/UserPortraitV2';
import TrackedButton from 'bundles/page/components/TrackedButton';
import { getActionUrl } from 'bundles/user-account/common/lib';

import _t from 'i18n!nls/composable-page-header';

type Props = {
  dropdownLinks?: Array<AccountDropdownLink>;
  footer?: React.ReactNode;
  header?: React.ReactNode;
  experienceSwitcher?: React.ReactNode;
  isCourseraPlusLearner?: boolean;
  hideFooterBorder?: boolean;
  courseId?: string;
  enterpriseOrgId?: string;
};

const styles = {
  linkItem: css`
    &:focus,
    &:hover {
      text-decoration: none;
    }
  `,
  linkItemContent: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--cds-spacing-100);
  `,
  logoutButton: css`
    height: 100%;
    width: 100%;
    background: none;
    text-align: left;
    border: none;
    padding: 0;

    &:focus {
      outline: none;
      border: none;
    }
  `,
  header: css`
    border-bottom: 1px solid var(--cds-color-grey-50);
    border-radius: var(--cds-border-radius-50);
    padding-bottom: var(--cds-spacing-100);
  `,
  hideFooterBorder: css`
    &.cds-popoverFooter-root {
      border: none;
      padding-top: 0;
    }
  `,
  popoverPadding: css`
    padding: var(--cds-spacing-100);
  `,
};

const AccountDropdownGridItemModule = ({
  dropdownLinks,
  footer,
  header,
  experienceSwitcher,
  isCourseraPlusLearner,
  hideFooterBorder,
  courseId,
  enterpriseOrgId,
}: Props) => {
  const [popoverAnchor, setPopoverAnchor] = React.useState<HTMLButtonElement | null>(null);
  const logoutFormRef = React.useRef<HTMLFormElement>(null);
  const trackV3Event = useTracker();

  const isOrgScopedHome = GrowthDiscoveryEpicClient.preview('orgScopedHome');

  const logoutFormKeyboardSubmit = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && logoutFormRef.current) {
      e.preventDefault();
      logoutFormRef.current.submit();
    }
  };

  const logoutFormClickSubmit = (e: React.MouseEvent) => {
    e.preventDefault();

    if (logoutFormRef.current) {
      logoutFormRef.current.submit();
    }
  };

  const linkItemClick = (e: React.MouseEvent, link: AccountDropdownLink) => {
    e.preventDefault();

    trackV3Event('click_profile_menu_item', {
      profileItemName: link.name ?? '',
      profileItemLink: link.href,
    });

    window.location.href = link.href;
  };

  React.useEffect(() => {
    if (popoverAnchor) {
      trackV3Event('open_navigation_menu', {
        navigationType: 'profile',
      });
    }
  }, [popoverAnchor, trackV3Event]);

  if (!isAuthenticatedUser()) {
    return null;
  }

  return (
    <div id="authenticated-info-menu">
      <IconButtonWrapper
        iconButton={
          <IconButton
            variant="ghost"
            icon={<UserPortrait user={userGet()} isCourseraPlusLearner={isCourseraPlusLearner} />}
            onClick={(e: { currentTarget: React.SetStateAction<HTMLButtonElement | null> }) =>
              setPopoverAnchor(e.currentTarget)
            }
            aria-expanded={Boolean(popoverAnchor)}
            aria-label={_t('User dropdown menu for #{learnerName}', { learnerName: userGet().fullName })}
            tooltip={_t('User dropdown menu for #{learnerName}', { learnerName: userGet().fullName })}
            size="small"
            data-e2e="header-profile"
          />
        }
        buttonSize={DEFAULT_ICON_BUTTON_HEIGHT}
        svgSize={DEFAULT_ICON_BUTTON_SVG_HEIGHT}
      />
      <Popover open={Boolean(popoverAnchor)} anchorElement={popoverAnchor} onClose={() => setPopoverAnchor(null)}>
        {header && <Popover.Header css={styles.header}>{header}</Popover.Header>}
        <Popover.Body component="div" tabIndex={0} css={isOrgScopedHome ? styles.popoverPadding : undefined}>
          <MenuList>
            {experienceSwitcher}
            {dropdownLinks?.map((link) => (
              <MenuList.ItemLink
                role="menuitem"
                href={link.href}
                key={link.name}
                target={link.openInNewWindow ? '_blank' : undefined}
                rel={link.openInNewWindow ? 'noopener noreferrer' : undefined}
                css={styles.linkItem}
                onClick={(e) => linkItemClick(e, link)}
                data-testid={`header-link-${link.name}`}
              >
                {link.withNewTag ? (
                  <div css={styles.linkItemContent}>
                    {link.label}
                    <Tag color="highlight1" priority="primary" variant="status">
                      {_t('New')}
                    </Tag>
                  </div>
                ) : (
                  link.label
                )}
              </MenuList.ItemLink>
            ))}
            {isOrgScopedHome && <LanguageTranslationMenuItem courseId={courseId} enterpriseOrgId={enterpriseOrgId} />}
            <MenuList.Item role="menuitem" onClick={logoutFormClickSubmit} onKeyDown={logoutFormKeyboardSubmit}>
              <form
                role="none"
                action={getActionUrl('logout', cookieGet('CSRF3-Token') || '')}
                method="post"
                ref={logoutFormRef}
              >
                <TrackedButton trackingName="logout_btn" type="submit" css={styles.logoutButton} tabIndex={-1}>
                  {_t('Log Out')}
                </TrackedButton>
              </form>
            </MenuList.Item>
          </MenuList>
        </Popover.Body>
        {footer && <Popover.Footer css={hideFooterBorder && styles.hideFooterBorder}>{footer}</Popover.Footer>}
      </Popover>
    </div>
  );
};

export default AccountDropdownGridItemModule;
